// frontend/src/utils/exportJobs.js
//
// How a generated export READS.
//
// An export past the server's row threshold becomes an ExportJob produced on
// the pipeline rail (backend: core/models.py ExportJob), listed on
// pages/Exports.jsx. Only the reading lives here; fetching is
// hooks/useExportJobs.js.

/**
 * How often a list of jobs re-asks while one is still being produced.
 *
 * Slower than the export control's own 2 s poll (utils/asyncExport.js):
 * nobody is watching a progress bar on a list, and the request costs a page of
 * rows rather than one.
 */
export const LIST_POLL_MS = 5000;

/**
 * Is this job still being produced? Pending and running both mean "wait".
 *
 * Read off the status rather than off `is_ready`: `is_ready` is false for a
 * FAILED job too, and a failed export is not one to keep waiting for.
 */
export const isGenerating = (job) => job.status === 'pending' || job.status === 'running';

/**
 * Should the list keep asking? Only while something is still being produced.
 *
 * Tolerates a missing list — before the first successful fetch there is no
 * list at all, and that is not "poll forever".
 */
export const shouldPoll = (jobs) => Array.isArray(jobs) && jobs.some(isGenerating);

/**
 * One file size, in the unit a merchant reads it in.
 *
 * Binary units (1024), because that is what every file manager reports — the
 * number here should match what they see after saving.
 *
 * `formatNumber` is injected rather than imported: the merchant's number
 * format comes from the `useFormatters` hook.
 *
 * NULL renders as an em dash, never "0 B" — the job has no file yet, and zero
 * would read as an export that found nothing. That is the same distinction
 * `size_bytes` keeps by staying NULL rather than 0.
 */
export function formatFileSize(bytes, formatNumber) {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${formatNumber(bytes / 1024, 0)} KB`;
  return `${formatNumber(bytes / (1024 * 1024), 1)} MB`;
}
